import { SanityNode } from "../../config/build/models/node";
import siteConfig from "../../config/siteConfig";
import { UrlMap } from "../../hooks/useUrlMap";
import { RouteInfo } from "../../models/shared/RouteInfo";
import { mapLanguageToUrlSegment } from "./urlLanguageMapper";

export const buildInternalUrl = (
  node: SanityNode,
  routeInfo: RouteInfo | undefined | null,
  urlMap?: UrlMap,
  urlInitialValue?: string,
  skipSlugCheck: boolean = false
): string => {
  // if we have an urlMap, look up path via the map instead of by parent
  if (urlMap && (node._id || node._ref)) {
    // _id is used for resolved nodes and _ref is used for unresolved references
    const ref = node._id ?? node._ref ?? "";
    let resolvedUrl = urlMap[ref];
    if (!resolvedUrl && process.env.NODE_ENV === "development") {
      resolvedUrl = urlMap[`drafts.${ref}`];
    }

    if (resolvedUrl) {
      return resolvedUrl;
    }
  }

  // without an urlMap (when creating pages), use lookup by parent
  let url = urlInitialValue ?? "/";

  if (node._type === "disclaimer") {
    // Disclaimer pages should not have their own slugs
    url = "/disclaimer";
  } else if (node._type === "notFoundError") {
    // Not found page should not have their own slugs
    url = "/404NotFound";
  } else if (node.slug?.current?.trim()?.length && !skipSlugCheck) {
    url = `/${node.slug.current}`;

    switch (node._type) {
      case "pressRelease":
        if (routeInfo?.pressReleaseList?.slug?.current?.trim().length) {
          url = `/${routeInfo.pressReleaseList?.slug?.current}${url}`;
        } else {
          console.warn("Press release list url missing!");
        }
        break;
      case "blogArticle":
        if (routeInfo?.blogList?.slug?.current?.trim().length) {
          url = `/${routeInfo.blogList?.slug?.current}${url}`;
        } else {
          console.warn("Blog list url missing!");
        }
        break;
      default:
        {
          let currentNode: SanityNode | undefined = node;

          while (
            (currentNode._rawParentLink ?? currentNode.parentLink)?.slug?.current?.length
          ) {
            currentNode = currentNode._rawParentLink ?? currentNode.parentLink;

            if (currentNode?.slug?.current?.length) {
              url = `/${currentNode.slug.current}${url}`;
            } else {
              break;
            }
          }
        }
        break;
    }
  }

  if (node.i18nLang && node.i18nLang?.toLowerCase() !== siteConfig.baseLang) {
    const languagePrefix = mapLanguageToUrlSegment(node.i18nLang);
    url = `/${languagePrefix}${url}`;
  }

  // Force trailing slash to match with Gatsby's generated paths
  if (!url.endsWith("/")) {
    url = `${url}/`;
  }

  return url.toLowerCase();
};
